
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { storeFollowedStrategy } from "@/utils/strategyUtils";

interface PremiumExistingActionProps {
  strategy: any;
  advantages: string[];
}

const PremiumExistingAction = ({ strategy, advantages }: PremiumExistingActionProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Store the followed strategy
        const success = await storeFollowedStrategy({
          strategy,
          userId: user.id,
          originalStrategyId: strategy.id || ""
        });
        
        if (!success) {
          throw new Error("Failed to store strategy information");
        }
        
        toast({
          title: "Success!",
          description: "You are now following this premium strategy",
        });
      } else {
        toast({
          title: "Authentication required",
          description: "Please log in to follow premium strategies",
          variant: "destructive"
        });
        navigate("/auth");
        return;
      }
      
      // Add a short delay before navigation for toast to be visible
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate directly to thank-you-premium page with strategy info
      navigate('/thank-you-premium', {
        state: {
          strategy
        }
      });
    } catch (error) {
      console.error("Error following strategy:", error);
      toast({
        title: "Error",
        description: "Failed to follow strategy. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Button 
      onClick={handleConfirm} 
      disabled={isSubmitting} 
      className="w-full bg-[#F6C528] hover:bg-[#F7D44C] text-[#012846] py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all"
    >
      {isSubmitting ? "Processing..." : "Follow Now"}
    </Button>
  );
};

export default PremiumExistingAction;
